/**
 * Menciones: separa lo que se GUARDA de lo que se VE mientras se escribe.
 *
 * El formato persistido es `@[Nombre](userId)` — lo lee `parseMentions` en el backend para
 * notificar, y `renderNoteContent` para pintar el chip en el timeline. Pero antd Mentions usa el
 * `value` de la opción como texto insertado en el textarea, así que al elegir a alguien del
 * autocomplete el operador veía el marcador crudo:
 *
 *     @[Martita Jaja](18723)          ← lo que se veía en el input
 *     @Martita Jaja                   ← lo que tiene que verse
 *
 * El textarea no puede mostrar un texto distinto de su value, así que el input pasa a trabajar con
 * el texto legible y el marcador se reconstruye al enviar, resolviendo el nombre contra la lista de
 * mencionables del registro.
 */

/** `@[Nombre](userId)`. Copia local del regex del render para no acoplar los dos módulos. */
const MARKER_RE = /@\[([^\]]+)\]\((\d+)\)/g;

export interface Mentionable {
  userId: number;
  name: string;
}

/** Marcadores → texto legible: `@[Martita Jaja](18723)` → `@Martita Jaja`. */
export function toDisplayText(raw: string): string {
  return (raw ?? "").replace(MARKER_RE, (_m, name: string) => `@${name}`);
}

/** Los mencionables que ya están citados en un texto guardado (por si alguno salió del registro). */
export function mentionsInText(raw: string): Mentionable[] {
  return [...(raw ?? "").matchAll(MARKER_RE)].map((m) => ({
    name: m[1],
    userId: Number(m[2]),
  }));
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Texto legible → marcadores, resolviendo cada `@Nombre` contra la lista.
 *
 * Tres detalles que importan:
 *
 * - Los nombres se ordenan de más largo a más corto: con "Ana" y "Ana Pérez" en la lista, `@Ana
 *   Pérez` tiene que resolver a la segunda y no dejar un " Pérez" suelto.
 * - Un `@Nombre` sólo cuenta si no sigue con letra o número, así `@Martita Jaja` no matchea dentro
 *   de `@Martita Jajason`.
 * - Si dos personas comparten nombre exacto gana la primera de la lista. No hay forma de
 *   desambiguar desde el texto; es el precio de mostrar el nombre en vez del id.
 */
export function toStoredText(display: string, mentionables: Mentionable[]): string {
  // Normaliza primero: si el texto ya trae marcadores (una nota que se está editando), se
  // convierten a legible para no terminar con `@[[Nombre](1)](1)`.
  const text = toDisplayText(display ?? "");
  const byName = new Map<string, number>();
  for (const m of mentionables) {
    if (m?.name && !byName.has(m.name)) byName.set(m.name, m.userId);
  }
  if (!byName.size) return text;
  const alternation = [...byName.keys()]
    .sort((a, b) => b.length - a.length)
    .map(escapeRe)
    .join("|");
  const re = new RegExp(`@(${alternation})(?![\\p{L}\\p{N}])`, "gu");
  return text.replace(re, (_m, name: string) => `@[${name}](${byName.get(name)})`);
}
